import React, { forwardRef, memo, useRef } from "react";
import { css } from "goober";

import { classNames } from "@/util/class-names.mjs";
import { useClientLayoutEffect } from "@/util/use-client-layout-effect.mjs";

import { ButtonLoadingSpinner } from "../LoadingSpinner/LoadingSpinner.js";

const cssTransition = () => css`
  transition:
    color var(--transition),
    background-color var(--transition),
    outline var(--transition),
    border-color var(--transition),
    box-shadow var(--transition);
`;

export const cssButton = () => css`
  :where(&) {
    --border: var(--shade6);
    --outline: transparent;
    --bg: var(--shade8);
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--sp-2);
    height: var(--btn-height, 2.25rem);
    padding-inline: var(--sp-3);
    white-space: nowrap;
    background-color: var(--bg);
    border-radius: var(--br);
    border: 1px solid var(--border) !important;
    outline: 2px solid var(--outline);
    outline-offset: 2px;
    cursor: pointer;
  }

  :where(&:hover) {
    --border: var(--shade3);
    --bg: var(--shade5-50);
  }

  :where(&:focus-visible) {
    --outline: var(--shade1-50);
    z-index: 3;
  }

  svg,
  img,
  .icon svg,
  .image img {
    height: var(--sp-4_5);
    width: var(--sp-4_5);
  }

  .icon-left {
    margin-left: -0.25rem;
  }
  .icon-right {
    margin-right: -0.25rem;
  }

  &[disabled] {
    opacity: 0.38;
    cursor: not-allowed;
  }

  &[data-loading="true"] {
    color: transparent !important;
    pointer-events: none;

    &[disabled] {
      opacity: 0.8;
    }
  }

  &.br-top-none {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  .overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &[data-block="true"] {
    display: flex;
    width: 100%;
  }

  /* Emphasis props */
  &[data-emphasis="high"] {
    --border: var(--primary);
    --outline: transparent;
    --bg: var(--primary);
    color: white; /* strickly for contrast against --primary */

    &:hover,
    &:focus-visible {
      --bg: var(--primary-hover);
    }
  }
  &[data-emphasis="low"] {
    --border: transparent;
    --outline: transparent;
    --bg: transparent;
    color: var(--shade2);

    &:hover,
    &:focus-visible {
      --border: transparent;
      --outline: transparent;
      --bg: var(--shade6);
      color: var(--shade0);
    }
  }

  /* Size props */
  &[data-size="large"],
  &[data-size="lg"] {
    height: var(--sp-11);
    padding: 0 var(--sp-6);

    svg,
    img {
      height: var(--sp-6);
      width: var(--sp-6);
      display: block;
    }
  }

  &[data-size^="small"],
  &[data-size^="sm"] {
    height: var(--sp-7);
    padding: 0;

    svg,
    img {
      height: var(--sp-3);
      width: var(--sp-3);
      display: block;
    }
  }

  .button-group & {
    --radius: 0;
    border-radius: 0;
  }

  .button-group &:first-child,
  .button-group &:last-child {
    --radius: var(--br);
  }

  .button-group &:first-child {
    border-top-left-radius: var(--radius);
    border-bottom-left-radius: var(--radius);
  }

  .button-group &:last-child {
    border-top-right-radius: var(--radius);
    border-bottom-right-radius: var(--radius);
  }

  .button-group &[data-active="true"],
  &[data-active="true"] {
    color: var(--shade0);
    background: var(--shade5);
    z-index: 2;
  }

  .button-group[data-block-group="true"] & {
    flex: 1;
  }
`;

export type ButtonSize = "small" | "sm" | "medium" | "large" | "lg";
export type ButtonEmphasis = "low" | "medium" | "high";

interface BaseButtonProps {
  size?: ButtonSize;
  emphasis?: ButtonEmphasis;
  block?: boolean;
  active?: boolean;
  textClass?: string;
  bgColor?: string;
  bgColorHover?: string;
  textColor?: string;
  textColorHover?: string;
  iconLeft?: string | React.ReactNode;
  iconRight?: string | React.ReactNode;
  loading?: boolean;
  className?: string;
  href?: string;
  target?: string;
  After?: React.ReactNode;
}

export type ButtonProps<T extends "a" | "button"> =
  React.ComponentPropsWithoutRef<T> & {
    as?: T;
  } & (T extends "a" ? BaseButtonProps & { href: string } : BaseButtonProps);

export const ButtonImpl = <T extends "a" | "button">(
  {
    href,
    target,
    as = (href ? "a" : "button") as T,
    size = "medium",
    emphasis = "medium",
    block = false,
    active,
    After,
    textClass,
    bgColor,
    bgColorHover,
    textColor,
    textColorHover,
    iconLeft,
    iconRight,
    children,
    loading,
    className,
    ...restProps
  }: ButtonProps<T>,
  ref: React.ForwardedRef<
    T extends "a" ? HTMLAnchorElement : HTMLButtonElement
  >,
) => {
  const buttonTextClass = textClass
    ? textClass
    : size === "large"
      ? "type-body1-form--active"
      : "type-form--button";

  const buttonProps = {
    "data-size": size,
    "data-emphasis": emphasis,
    "data-block": block.toString(),
    "data-active": active,
    "data-loading": loading,
    href,
    target,
    ...(target === "_blank" && { rel: "noreferrer" }),
    type: "button",
    ...restProps,
  };

  const cssOverrides = css({
    [`&&[data-emphasis^="${emphasis}"]`]: {
      background: bgColor,
      color: textColor,

      "&&:hover": {
        background: bgColorHover,
        color: textColorHover,
      },

      "&&:focus-visible": {
        background: bgColorHover,
        color: textColorHover,
      },
    },
  });

  const ButtonElem = as as unknown as (
    // nice as.
    e: JSX.IntrinsicElements[T],
  ) => JSX.Element;

  const internalRef = useRef(null);

  // This is not pretty, but adding the transition property *after* the element
  // is inserted in the DOM ensures that we don't get a flicker from the unstyled
  // element to the new style.
  useClientLayoutEffect(() => {
    requestAnimationFrame(() => {
      (
        (ref ?? internalRef) as React.MutableRefObject<HTMLElement>
      ).current?.classList.add(cssTransition());
    });
  }, [ref]);

  return (
    <ButtonElem
      ref={ref ?? internalRef}
      {...classNames(cssButton(), cssOverrides, className)}
      {...buttonProps}
    >
      {iconLeft && <span className="icon-left">{iconLeft}</span>}
      <span className={buttonTextClass}>{children}</span>
      {iconRight && <span className="icon-right">{iconRight}</span>}

      {After}

      {loading && (
        <div className="overlay">
          <ButtonLoadingSpinner />
        </div>
      )}
    </ButtonElem>
  );
};

export const Button = memo(forwardRef(ButtonImpl));

// Button group
export const cssButtonGrp = () => css`
  display: flex;
  align-items: center;
  gap: 1px;
  gap: 0;

  a,
  button {
    position: relative;
    color: var(--shade1);
    border-radius: 0;
    border-right: none !important;
    box-shadow: 1px 0 var(--border);

    &:hover {
      --border: var(--shade3);
      color: var(--shade0) !important;
      background: var(--shade6) !important;
      z-index: 1;
    }
  }

  a.actiove,
  a[data-active="true"],
  button.active,
  button[data-active="true"] {
    --border: var(--shade3);
    color: var(--shade0) !important;
    background: var(--shade6) !important;
    z-index: 2;
  }

  > :first-child {
    border-top-left-radius: var(--br) !important;
    border-bottom-left-radius: var(--br) !important;
  }

  > :last-child {
    border-top-right-radius: var(--br) !important;
    border-bottom-right-radius: var(--br) !important;
  }

  &[data-block-group="true"] {
    width: 100%;

    > * {
      flex: 1;
      justify-content: center;
    }
  }
`;

interface ButtonGroupProps {
  block?: boolean;
  className?: string;
  children?: React.ReactNode;
  [x: string]: unknown;
}

export const ButtonGroup = ({
  block = false,
  className,
  children,
  ...rest
}: ButtonGroupProps) => {
  return (
    <div
      data-block-group={block.toString()}
      {...classNames(className, cssButtonGrp(), "button-group")}
      {...rest}
    >
      {children}
    </div>
  );
};
